/* Core & libraries */
import React, {useEffect, useState} from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { usePaymentService } from '../../services/paymentService.js';
import { useNotificationContext } from "../../contexts/notificationContext";
import { formatExpDate, isScriptLoaded } from "../../helpers/general";
import { AA_FOP_SCRIPT, AIRLINES } from "../../constants/appConstants";
import { useUserContext } from "../../contexts/userContext";
import CardModal from "../../components/modals/cardModal";
import { useGlobalContext } from "../../contexts/globalContext.js";
import { useGlobalUpdateContext } from "../../contexts/globalContext.js";

/* Components */
import PaymentPageHeader from "../../components/paymentPageHeader";
import PaymentTabbedNav from "../../components/paymentTabbedNav/index.js";

/* Styles */
import styles from "./styles.js";

const Payment = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const globalProperties  = useGlobalContext();
  const setGlobalProperties = useGlobalUpdateContext();

  const { getCartId, getPaymentList, addPaymentCard, editPaymentCard, paymentToken, getPaymentToken } = usePaymentService();
  const { setNotification } = useNotificationContext();
  const [paymentResponseData, setPaymentResponseData] = useState({})
  const [showCardModal, setShowCardModal] = useState(false)
  const [referenceId, setReferenceId] = useState("")
  const [fopUUIDState, setFopUUIDState] = useState("")

  useEffect(() => {
    if (!showCardModal) {
      setReferenceId("")
      setFopUUIDState("")
    }
  }, [showCardModal]);

  const getCardType = cardType => {
    switch(cardType) {
      case "MASTER_CARD":
        return "CA"
      case "VISA":
        return "VI"
      case "AMERICAN_EXPRESS":
        return "AX"
      case "DISCOVER":
        return "DS"
      case "DINERS_CLUB":
        return "DC"
      case "UNIVERSAL_AIR_TRAVEL_CARD":
        return "TP"
      case "JCB":
        return "JC"
      case "CHINA_UNIONPAY":
        return "CU"
      default:
        return cardType
    }
  }

  const getCardText = cardType => {
    switch(cardType) {
      case "MASTER_CARD":
        return "Mastercard"
      case "VISA":
        return "Visa"
      case "AMERICAN_EXPRESS":
        return "American Express"
      case "DISCOVER":
        return "Discover"
      case "DINERS_CLUB":
        return "Diners Club"
      case "UNIVERSAL_AIR_TRAVEL_CARD":
        return "Universal Air Travel"
      case "JCB":
        return "JCB"
      case "CHINA_UNIONPAY":
        return "China Union Pay"
      case "VIAS":
        return "Alaska Visa"
      default:
        return cardType
    }
  }

  const callback = async (response, type, fopUUID) => {
    switch (providerCode) {
      case AIRLINES.AA:
        if(response?.status === "SUCCESS") {
          if(type === "add") {
            let payload = {
              fopType: "CC",
              cardType: getCardType(response?.data?.cardType),
              expDate: formatExpDate(response?.data?.expirationMonth, response?.data?.expirationYear),
              lastFourDigits: response?.data?.cardNumberLastFour,
              referenceId: response?.data?.referenceId,
              nickname: response?.data?.cardNickname
            };
            const addResponse = await addPaymentCard(payload)
            if(addResponse) {
              //If we have a card added set global properties so that we can display the assign modal right after.
              setGlobalProperties({
                ...globalProperties,
                addFirstCardData: {
                  ...globalProperties.addFirstCardData,
                  firstTimeCardAdded: true,
                  cardType: getCardType(response?.data?.cardType),
                  cardNumberMasked: response?.data?.cardNumberLastFour,
                  expirationDate: formatExpDate(response?.data?.expirationMonth, response?.data?.expirationYear),
                  nickname: response?.data?.cardNickname,
                }
              })
              getPaymentList({page: 1}, setPaymentResponseData)
            }
          } else if(type === "edit") {
              let payload = {
                expDate: formatExpDate(response?.data?.expirationMonth, response?.data?.expirationYear)
              };
              const editResponse = await editPaymentCard(payload, fopUUID)
              if(editResponse) {
                getPaymentList({page: 1}, setPaymentResponseData)
                setNotification(
                  "success",
                  <FormattedMessage id="notification.editPaymentSuccessTitle" />,
                  <FormattedMessage id="notification.editPaymentSuccessText" values={{cardType: getCardText(response?.data?.cardType), cardNumber: response?.data?.cardNumberLastFour}}/>
                )
              }
          }
        }
        break;
      case AIRLINES.AM:
      case AIRLINES.AS:
      case AIRLINES.UC:
        const expDate = response?.data?.expirationMonth && response?.data?.expirationYear ?
          formatExpDate(parseInt(response?.data?.expirationMonth), parseInt(response?.data?.expirationYear)) : ""
        let payload = {
          fopType: "CC",
          cardType: response?.data?.cardType,
          expDate: expDate,
          lastFourDigits: response?.data?.lastFourDigits,
          referenceId: response?.data?.referenceId || referenceId,
          nickname: response?.data?.nickname
        };

        if(!referenceId) {
          const addResponse = await addPaymentCard(payload)
          if (addResponse) {
            getPaymentList({page: 1}, setPaymentResponseData)
          }
        } else {
          const updateResponse = await editPaymentCard(payload, fopUUIDState)
          if (updateResponse) {
            getPaymentList({page: 1}, setPaymentResponseData)
            setNotification(
              "success",
              <FormattedMessage id="notification.editPaymentSuccessTitle" />,
              <FormattedMessage id="notification.editPaymentSuccessText" values={{cardType: getCardText(response?.data?.cardType), cardNumber: response?.data?.cardNumberLastFour}}/>
            )
          }
        }
        break;
    }
  }

  const addPayment = async (spinner) => {
    switch (providerCode) {
      case AIRLINES.AA:
        const response = await getCartId(spinner, true);
        if(response && isScriptLoaded(AA_FOP_SCRIPT)) {
          window.getCheckoutApp && window.getCheckoutApp(response?.cardId, (data) => callback(data, "add"), response?.authType || "EMPLOYEE")
        } else {
          setNotification(
            "error",
            <FormattedMessage id="notifications.revokeErrorTitle" />,
            <FormattedMessage id="notifications.revokeErrorText" />
          );
        }
        break;
      case AIRLINES.AM:
      case AIRLINES.AS:
      case AIRLINES.UC:
        const resp = await getPaymentToken({spinner, payload: {externalReferenceId: ""}})
        if (resp) {
          setShowCardModal(true)
        }
        break;
    }
  };

  const editPayment = async (spinner, fopUUID, referenceId = "") => {

    switch (providerCode) {
      case AIRLINES.AA:
        const response = await getCartId(spinner, false, fopUUID);
        if(response && isScriptLoaded(AA_FOP_SCRIPT)) {
          window.getCheckoutApp && window.getCheckoutApp(response?.cardId, (data) => callback(data, "edit", fopUUID), response?.authType || "EMPLOYEE")
        } else {
          setNotification(
            "error",
            <FormattedMessage id="notifications.revokeErrorTitle" />,
            <FormattedMessage id="notifications.revokeErrorText" />
          );
        }
        break;
      case AIRLINES.AM:
      case AIRLINES.AS:
      case AIRLINES.UC:
        const resp = await getPaymentToken({spinner, payload: {externalReferenceId: referenceId }})
        if (resp) {
          setReferenceId(referenceId)
          setFopUUIDState(fopUUID)
          setShowCardModal(true)
        }
        break;
    }
  };

  const viewPayment = async (spinner, fopUUID, referenceId = "") => {

    switch (providerCode) {
      case AIRLINES.AM:
      case AIRLINES.AS:
        const resp = await getPaymentToken({spinner, payload: {externalReferenceId: referenceId }})
        if (resp) {
          setReferenceId(referenceId)
          setFopUUIDState(fopUUID)
          setShowCardModal(true)
        }
        break;
    }
  };

  return (
    <div className={classes.paymentPage}>
      <PaymentPageHeader addPayment={addPayment}/>
      <PaymentTabbedNav addPayment={addPayment} editPayment={editPayment} viewPayment={viewPayment} paymentResponseData={paymentResponseData}/>
      {
        showCardModal && AIRLINES.AA !== providerCode &&
        <CardModal
          showModal={showCardModal}
          setShowModal={setShowCardModal}
          paymentToken={paymentToken}
          modalTitle={referenceId ? <FormattedMessage id="payment.viewCardTitle" /> : <FormattedMessage id="payment.addCardTitle" />}
          modalSubtitle={referenceId ? <FormattedMessage id={`payment.${providerCode}.viewCardSubtitle`} /> : null}
          callback={callback}
        />
      }
    </div>
  );
};

export default Payment;
